import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Typeahead } from "@/components/Typeahead";
import { toast } from "sonner";
import { Plus, DollarSign, Edit, Trash2, Search } from "lucide-react";
import { PageHeader, StatusBadge, formatDate } from "@/lib/ui";

const STATUSES = ["draft", "sent", "paid", "unpaid", "overdue"];

export default function Billing() {
  const { currentOrgId, currentRole, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState(blank());
  const isReadOnly = currentRole === "read_only";

  function blank() {
    return {
      client_id: "", service: "", service_date: new Date().toISOString().slice(0, 10),
      hours: "1", rate: "0", staff_user_id: user?.id || "", status: "draft", notes: "",
    };
  }

  const load = async () => {
    setLoading(true);
    const [e, c, m] = await Promise.all([
      supabase.from("billing_entries")
        .select("*, clients(id, first_name, last_name), profiles:staff_user_id(full_name)")
        .eq("organization_id", currentOrgId).order("service_date", { ascending: false }),
      supabase.from("clients").select("id, first_name, last_name, email, phone").eq("organization_id", currentOrgId).eq("archived", false).order("last_name"),
      supabase.from("organization_members").select("user_id").eq("organization_id", currentOrgId).eq("status", "active"),
    ]);
    setEntries(e.data || []);
    setClients(c.data || []);
    const ids = (m.data || []).map((x) => x.user_id);
    if (ids.length) {
      const { data: profs } = await supabase.from("profiles").select("id, full_name").in("id", ids);
      setStaff((profs || []).map((p) => ({ user_id: p.id, full_name: p.full_name || "Team member" })));
    } else setStaff([]);
    setLoading(false);
  };
  useEffect(() => { if (currentOrgId) load(); /* eslint-disable-next-line */ }, [currentOrgId]);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      client_id: b.client_id, service: b.service, service_date: b.service_date,
      hours: String(b.hours || 0), rate: String(b.rate || 0),
      staff_user_id: b.staff_user_id || "", status: b.status, notes: b.notes || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.client_id) return toast.error("Select a client");
    if (!form.service.trim()) return toast.error("Service is required");
    const payload = {
      ...form, hours: Number(form.hours) || 0, rate: Number(form.rate) || 0,
      staff_user_id: form.staff_user_id || null,
    };
    try {
      if (editing) {
        await apiFetch(`/billing-entries/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast.success("Billing entry updated");
      } else {
        await apiFetch("/billing-entries", { method: "POST", body: JSON.stringify({ ...payload, organization_id: currentOrgId }) });
        toast.success("Billing entry created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete this billing entry for "${b.service}"?`)) return;
    try { await apiFetch(`/billing-entries/${b.id}`, { method: "DELETE" }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const liveTotal = ((Number(form.hours) || 0) * (Number(form.rate) || 0)).toFixed(2);

  const filtered = useMemo(() => entries.filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    const client = b.clients ? `${b.clients.first_name} ${b.clients.last_name}`.toLowerCase() : "";
    return (b.service || "").toLowerCase().includes(ql) || client.includes(ql);
  }), [entries, q, statusFilter]);

  const total = filtered.reduce((s, e) => s + (Number(e.total) || 0), 0);
  const paid = filtered.filter((e) => e.status === "paid").reduce((s, e) => s + (Number(e.total) || 0), 0);
  const outstanding = filtered.filter((e) => ["sent", "unpaid", "overdue"].includes(e.status)).reduce((s, e) => s + (Number(e.total) || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="billing-page">
      <PageHeader title="Client Billing" subtitle="Service-based billing entries for your clients."
        actions={!isReadOnly && (
          <Button data-testid="new-billing-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
            <Plus size={14} className="mr-1.5" /> New billing entry
          </Button>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label="Total billed" value={`$${total.toFixed(2)}`} />
        <Kpi label="Paid" value={`$${paid.toFixed(2)}`} />
        <Kpi label="Outstanding" value={`$${outstanding.toFixed(2)}`} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by service or client…" className="pl-9 bg-white border-[#E8E2D9]" data-testid="billing-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="billing-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E2D9]">
          <DollarSign size={26} className="mx-auto text-[#A8A29E] mb-3" strokeWidth={1.5} />
          <p className="text-sm font-medium">{entries.length === 0 ? "No billing entries yet" : "No entries match your filter"}</p>
          <p className="text-xs text-[#57534E] mt-1">Create the first entry using the button above.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Client</th>
                <th className="text-left px-5 py-3">Service</th>
                <th className="text-left px-5 py-3">Staff</th>
                <th className="text-right px-5 py-3">Hours</th>
                <th className="text-right px-5 py-3">Rate</th>
                <th className="text-right px-5 py-3">Total</th>
                <th className="text-left px-5 py-3">Status</th>
                {!isReadOnly && <th />}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {filtered.map((b) => (
                <tr key={b.id} className="hover:bg-[#FAF8F5] cursor-pointer" data-testid={`bill-row-${b.id}`} onClick={() => openEdit(b)}>
                  <td className="px-5 py-3">{formatDate(b.service_date)}</td>
                  <td className="px-5 py-3">{b.clients ? <Link to={`/app/clients/${b.clients.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{b.clients.first_name} {b.clients.last_name}</Link> : "—"}</td>
                  <td className="px-5 py-3 font-medium">{b.service}</td>
                  <td className="px-5 py-3 text-[#57534E]">{b.profiles?.full_name || "—"}</td>
                  <td className="px-5 py-3 text-right">{Number(b.hours).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right">${Number(b.rate).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right font-semibold">${Number(b.total).toFixed(2)}</td>
                  <td className="px-5 py-3"><StatusBadge value={b.status} /></td>
                  {!isReadOnly && (
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" data-testid={`edit-bill-${b.id}`} onClick={(e) => { e.stopPropagation(); openEdit(b); }}><Edit size={13} /></Button>
                      <Button size="sm" variant="ghost" data-testid={`del-bill-${b.id}`} onClick={(e) => { e.stopPropagation(); remove(b); }} className="text-red-600"><Trash2 size={13} /></Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit billing entry" : "New billing entry"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <div className="mt-1.5">
                <Typeahead testid="bill-client-typeahead"
                  options={clients.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name}`, sub: c.email || c.phone || "" }))}
                  value={form.client_id}
                  onChange={(v) => setForm({ ...form, client_id: v || "" })}
                  placeholder="Search clients…" />
              </div>
            </div>
            <div><Label>Service / task *</Label>
              <Input value={form.service} onChange={(e) => setForm({...form, service: e.target.value})} className="mt-1.5" placeholder="e.g. Care coordination call" data-testid="bill-service" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label>
                <Input type="date" value={form.service_date} onChange={(e) => setForm({...form, service_date: e.target.value})} className="mt-1.5" data-testid="bill-date" />
              </div>
              <div><Label>Staff member</Label>
                <div className="mt-1.5">
                  <Typeahead testid="bill-staff-typeahead"
                    options={staff.map((s) => ({ value: s.user_id, label: s.full_name }))}
                    value={form.staff_user_id}
                    onChange={(v) => setForm({ ...form, staff_user_id: v || "" })}
                    placeholder="Search staff…" />
                </div>
              </div>
              <div><Label>Hours / quantity *</Label>
                <Input type="number" step="0.01" value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} className="mt-1.5" data-testid="bill-hours" />
              </div>
              <div><Label>Rate ($) *</Label>
                <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({...form, rate: e.target.value})} className="mt-1.5" data-testid="bill-rate" />
              </div>
            </div>
            <div className="bg-[#FAF8F5] rounded-lg p-3 flex items-center justify-between border border-[#E8E2D9]">
              <span className="text-sm text-[#57534E]">Total (auto-calculated)</span>
              <span className="text-lg font-display font-bold" data-testid="bill-total">${liveTotal}</span>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1.5" data-testid="bill-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5" data-testid="bill-notes" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {editing && (
              <Button variant="outline" onClick={() => { remove(editing).then(() => setOpen(false)); }}
                      className="rounded-full text-red-600 border-red-200 mr-auto" data-testid="delete-bill-btn">
                <Trash2 size={13} className="mr-1" /> Delete
              </Button>
            )}
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="save-bill-btn" onClick={save} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{editing ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D9] p-4">
      <div className="text-xs text-[#A8A29E] uppercase tracking-widest">{label}</div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
    </div>
  );
}
